import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { useLogoSettings } from '@/hooks/use-logo-settings';
import { AppSettingsService } from '@/services/app-settings-service';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Mail, Upload, Globe, Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export const EmailBrandingManagement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentLogoUrl, updateLogo } = useLogoSettings();

  // Email Template Logo state
  const [emailLogoUrl, setEmailLogoUrl] = useState('');
  
  // Template normalization state
  const [isNormalizingTemplates, setIsNormalizingTemplates] = useState(false);

  // Get email template logo
  const { data: emailTemplateLogo, isLoading: isLoadingEmailLogo } = useQuery({
    queryKey: ['email-template-logo'],
    queryFn: () => AppSettingsService.getEmailTemplateLogo(),
  });

  // Get BIMI logo (if exists)
  const { data: bimiLogoUrl, isLoading: isLoadingBimi } = useQuery({
    queryKey: ['bimi-logo'],
    queryFn: () => AppSettingsService.getSetting('email_bimi_logo_url'),
  });

  // Update email template logo mutation
  const updateEmailLogoMutation = useMutation({
    mutationFn: (logoUrl: string) => AppSettingsService.updateEmailTemplateLogo(logoUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-template-logo'] });
      toast({
        title: "Email Logo Updated",
        description: "Email template logo has been updated successfully.",
      });
      setEmailLogoUrl('');
    },
    onError: (error) => {
      console.error('Email logo update failed:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update email template logo. Please try again.",
        variant: "destructive"
      });
    },
  });

  // Update BIMI logo mutation
  const updateBimiLogoMutation = useMutation({
    mutationFn: (logoUrl: string) => AppSettingsService.getSetting('email_bimi_logo_url').then(() => {
      // For now, we'll just show a message since BIMI setup is more complex
      return Promise.resolve(true);
    }),
    onSuccess: () => {
      toast({
        title: "BIMI Setup",
        description: "BIMI logo configuration requires additional DNS setup. Contact support for assistance.",
        variant: "default"
      });
    },
  });

  const handleUpdateEmailLogo = () => {
    if (!emailLogoUrl.trim()) {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid logo URL.",
        variant: "destructive"
      });
      return;
    }
    updateEmailLogoMutation.mutate(emailLogoUrl.trim());
  };

  React.useEffect(() => {
    if (emailTemplateLogo) {
      setEmailLogoUrl(emailTemplateLogo);
    }
  }, [emailTemplateLogo]);

  // Handle template normalization
  const handleNormalizeTemplates = async (previewOnly: boolean = true) => {
    setIsNormalizingTemplates(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('normalize-email-templates', {
        body: { preview_only: previewOnly }
      });

      if (error) {
        throw error;
      }

      if (previewOnly) {
        toast({
          title: "Template Analysis Complete",
          description: `Found ${data.templates_affected} templates that need normalization. Check the console for details.`,
        });
        console.log('Template normalization preview:', data);
      } else {
        toast({
          title: "Templates Normalized",
          description: `Successfully updated ${data.templates_affected} templates with current logo.`,
        });
        // Refresh any template-related queries if needed
        queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      }
    } catch (error) {
      console.error('Template normalization failed:', error);
      toast({
        title: "Normalization Failed",
        description: `Failed to ${previewOnly ? 'preview' : 'apply'} template changes. Please try again.`,
        variant: "destructive"
      });
    } finally {
      setIsNormalizingTemplates(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Mail className="h-6 w-6" />
          Email Branding Management
        </h2>
        <p className="text-muted-foreground mt-2">
          Manage logos used in different email contexts for optimal branding and deliverability
        </p>
      </div>

      {/* System Logo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            System Logo
          </CardTitle>
          <CardDescription>
            Logo displayed in the web interface and system pages
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {currentLogoUrl && (
            <div className="flex items-center gap-4">
              <img
                src={currentLogoUrl}
                alt="Current system logo"
                className="h-12 w-auto max-w-[200px] object-contain border rounded"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="text-sm text-muted-foreground">
                Current system logo
              </div>
            </div>
          )}
          <p className="text-sm text-muted-foreground">
            Managed in the Logo Management tab. This logo appears in the application interface.
          </p>
        </CardContent>
      </Card>

      <Separator />

      {/* Email Template Logo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Template Logo
          </CardTitle>
          <CardDescription>
            Logo embedded in email content for consistent branding
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {emailTemplateLogo && (
            <div className="flex items-center gap-4">
              <img
                src={emailTemplateLogo}
                alt="Current email template logo"
                className="h-12 w-auto max-w-[200px] object-contain border rounded"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="text-sm text-muted-foreground">
                Current email template logo
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="email-logo-url">Email Template Logo URL</Label>
            <div className="flex gap-2">
              <Input
                id="email-logo-url"
                placeholder="https://example.com/email-logo.png"
                value={emailLogoUrl}
                onChange={(e) => setEmailLogoUrl(e.target.value)}
                disabled={isLoadingEmailLogo || updateEmailLogoMutation.isPending}
              />
              <Button
                onClick={handleUpdateEmailLogo}
                disabled={isLoadingEmailLogo || updateEmailLogoMutation.isPending || !emailLogoUrl.trim()}
              >
                <Upload className="h-4 w-4 mr-2" />
                Update
              </Button>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Email Logo Requirements:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Format: PNG or JPG</li>
              <li>• Recommended width: 150-300px</li>
              <li>• File size: Under 100KB for fast loading</li>
              <li>• Use HTTPS URL for security</li>
              <li>• Ensure logo is publicly accessible</li>
            </ul>
          </div>

          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-medium text-green-900 mb-2">Usage in Templates:</h4>
            <p className="text-sm text-green-800">
              Use <code className="bg-green-200 px-1 rounded">{'{{email_logo_url}}'}</code> in your email templates to automatically include this logo.
            </p>
          </div>

          {/* Template Normalization Section */}
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <h4 className="font-medium text-orange-900 mb-2">Template Normalization</h4>
            <p className="text-sm text-orange-800 mb-3">
              Clean up existing templates to use the current logo and remove old logo references.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleNormalizeTemplates(true)}
                disabled={isNormalizingTemplates}
              >
                {isNormalizingTemplates ? 'Checking...' : 'Preview Changes'}
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => handleNormalizeTemplates(false)}
                disabled={isNormalizingTemplates}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {isNormalizingTemplates ? 'Applying...' : 'Apply Changes'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* BIMI Logo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            BIMI Logo (Brand Indicators for Message Identification)
          </CardTitle>
          <CardDescription>
            Official brand logo for email authentication and deliverability
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {bimiLogoUrl && (
            <div className="flex items-center gap-4">
              <img
                src={bimiLogoUrl}
                alt="Current BIMI logo"
                className="h-12 w-auto max-w-[200px] object-contain border rounded"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="text-sm text-muted-foreground">
                Current BIMI logo
              </div>
            </div>
          )}

          <div className="bg-amber-50 p-4 rounded-lg">
            <h4 className="font-medium text-amber-900 mb-2">BIMI Setup Requirements:</h4>
            <ul className="text-sm text-amber-800 space-y-1">
              <li>• SVG format with specific specifications</li>
              <li>• Square aspect ratio (1:1)</li>
              <li>• Trademark certificate verification</li>
              <li>• DNS TXT record configuration</li>
              <li>• DMARC policy enforcement</li>
            </ul>
          </div>

          <Button 
            variant="outline" 
            onClick={() => updateBimiLogoMutation.mutate('')}
            disabled={updateBimiLogoMutation.isPending}
          >
            <Shield className="h-4 w-4 mr-2" />
            Setup BIMI (Contact Support)
          </Button>

          <p className="text-sm text-muted-foreground">
            BIMI implementation requires professional email authentication setup. Contact support for assistance with DMARC, DKIM, and BIMI configuration.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};